import { TransactionType, TransactionProxyState } from "@lib/types";
import { Steps } from "antd";
import { useIntl } from "react-intl";
import { useMemo } from "react";
import { StepProps } from "antd/lib";

type Props = {
  transaction?: TransactionType;
  isCreateMode: boolean;
};

export default function WorkflowSteps({ transaction, isCreateMode }: Props) {
  const { messages } = useIntl();

  const stepStatus1 = useMemo(() => {
    if (isCreateMode) return "process";
    return "finish";
  }, [isCreateMode]);

  const stepStatus2 = useMemo(() => {
    if (transaction?.state === TransactionProxyState.PendingForAccountInfo)
      return "process";
    if (transaction?.providerTransactionConfigId) return "finish";
    return "wait";
  }, [transaction?.state, transaction?.providerTransactionConfigId]);

  const stepStatus3 = useMemo(() => {
    if (transaction?.state === TransactionProxyState.PendingCustomerApproval)
      return "process";
    if (
      transaction?.state === TransactionProxyState.ApprovedByCustomer ||
      transaction?.state === TransactionProxyState.ApprovedByProvider ||
      transaction?.state === TransactionProxyState.PendingProviderApproval
    )
      return "finish";
    return "wait";
  }, [transaction?.state]);

  const stepStatus4 = useMemo(() => {
    if (transaction?.state === TransactionProxyState.PendingProviderApproval)
      return "process";
    if (transaction?.state === TransactionProxyState.ApprovedByProvider)
      return "finish";
    return "wait";
  }, [transaction?.state]);

  const workflowSteps = useMemo(() => {
    const steps: StepProps[] = [
      {
        title: messages["common.create"] as string,
        description: messages["common.paymentRequest"] as string,
        status: stepStatus1,
      },
      {
        title: messages["common.configure"] as string,
        description: messages["common.providerTransactionConfig"] as string,
        status: stepStatus2,
      },
      {
        title: messages["common.payment"] as string,
        description: messages["common.customerPayment"] as string,
        status: stepStatus3,
      },
      {
        title: messages["common.approve"] as string,
        description: messages["common.providerApproval"] as string,
        status: stepStatus4,
      },
    ];
    return steps;
  }, [transaction, isCreateMode, messages]);

  const currentStage = useMemo(() => {
    if (isCreateMode) return 0;

    if (transaction?.state === TransactionProxyState.PendingForAccountInfo)
      return 1;

    if (transaction?.state === TransactionProxyState.PendingCustomerApproval)
      return 2;

    if (transaction?.state === TransactionProxyState.PendingProviderApproval)
      return 3;

    if (
      transaction?.state === TransactionProxyState.ApprovedByProvider ||
      transaction?.state === TransactionProxyState.ApprovedByCustomer
    )
      return 4;

    return 1;
  }, [transaction, isCreateMode]);

  return <Steps current={currentStage} items={workflowSteps} size="small" />;
}
